import { containsProductKey } from "./product-key-redaction";

const storagePrefix = "tb-license-platform:product-key-replacement-recovery:";
const recoveryTTL = 30 * 24 * 60 * 60 * 1000;

export type ProductKeyReplacementRecovery = {
  licenseID: string;
  keyID: string;
  reason: string;
  idempotencyKey: string;
  expiresAt: number;
};

function storageKey(actorID: string) {
  return `${storagePrefix}${actorID}`;
}

export function readProductKeyReplacementRecovery(actorID: string): ProductKeyReplacementRecovery | null {
  const key = storageKey(actorID);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ProductKeyReplacementRecovery>;
    if (
      typeof parsed.licenseID !== "string" || !parsed.licenseID ||
      typeof parsed.keyID !== "string" || !parsed.keyID ||
      typeof parsed.reason !== "string" || !parsed.reason.trim() || containsProductKey(parsed.reason) ||
      typeof parsed.idempotencyKey !== "string" ||
      parsed.idempotencyKey.length < 8 || parsed.idempotencyKey.length > 128 ||
      typeof parsed.expiresAt !== "number" ||
      parsed.expiresAt <= Date.now()
    ) {
      sessionStorage.removeItem(key);
      return null;
    }
    return {
      licenseID: parsed.licenseID,
      keyID: parsed.keyID,
      reason: parsed.reason,
      idempotencyKey: parsed.idempotencyKey,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Session storage may be unavailable rather than malformed.
    }
    return null;
  }
}

export function writeProductKeyReplacementRecovery(actorID: string, recovery: Omit<ProductKeyReplacementRecovery, "expiresAt">) {
  if (containsProductKey(recovery.reason)) return;
  try {
    sessionStorage.setItem(
      storageKey(actorID),
      JSON.stringify({ ...recovery, expiresAt: Date.now() + recoveryTTL }),
    );
  } catch {
    // The in-memory attempt remains usable when session storage is unavailable.
  }
}

export function clearProductKeyReplacementRecovery(actorID: string) {
  try {
    sessionStorage.removeItem(storageKey(actorID));
  } catch {
    // Storage cleanup is best effort; server-side replay still expires after 30 days.
  }
}
